import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import Loader from '@tcl-boron-prefabs/loader';
import { Note } from '@tcl-boron-icons/icons';
import { ChevronRight } from '@tcl-boron-icons/icons';
import { BrandPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import { createUseStyles } from 'react-jss';
import { PortalContainer } from '../../components/PortalContainer';
import ErrorScreen from '../../components/ErrorScreen';
import usePortalContext from '../../context/usePortalContext';
import { PortalAttachment } from '../../context/interfaces/attachment';
import { NavigationPath } from '../../../api/constants';
import logger from '../../../../utils/logger';

const useStyles = createUseStyles({
  page: {
    width: '100%',
    maxWidth: '960px',
    margin: '0 auto',
    padding: '24px 0 48px',
    boxSizing: 'border-box',
    minWidth: 0,
    '@media (min-width: 600px)': {
      padding: '32px 0 64px',
    },
  },
  header: {
    marginBottom: '8px',
  },
  subheader: {
    color: GrayscalePalette[600],
    marginBottom: '24px',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
  },
  card: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'white',
    border: `1px solid ${GrayscalePalette[200]}`,
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    flex: '0 0 auto',
    maxWidth: '100%',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
    '&:hover, &:focus-visible': {
      borderColor: BrandPalette[400],
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-1px)',
      outline: 'none',
    },
  },
  iconWrap: {
    flexShrink: 0,
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: GrayscalePalette[100],
    color: BrandPalette[400],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    flexGrow: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  cardMeta: {
    color: GrayscalePalette[600],
    marginTop: '2px',
  },
  chevron: {
    flexShrink: 0,
    color: GrayscalePalette[500],
  },
  centerState: {
    flexGrow: 1,
    minHeight: '320px',
  },
  emptyState: {
    flexGrow: 1,
    minHeight: '320px',
    color: GrayscalePalette[600],
    gap: '12px',
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: GrayscalePalette[100],
    color: GrayscalePalette[500],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const formatDate = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const Documents: FunctionComponent = () => {
  const { fetchHelper } = usePortalContext();
  const { push } = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [attachments, setAttachments] = useState<PortalAttachment[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        const response = await fetchHelper<PortalAttachment[]>('/attachment/all');
        setAttachments(response);
      } catch (e) {
        logger.error(e as Error, 'Error fetching documents');
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAttachments();
  }, []);

  const sortedAttachments = useMemo(
    () =>
      attachments
        ? [...attachments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        : undefined,
    [attachments],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Grid
          container
          direction="column"
          gap="8px"
          alignItems="center"
          justifyContent="center"
          className={classes.centerState}
        >
          <Loader size="large" />
          <Typography variant="body1">Loading documents</Typography>
        </Grid>
      );
    }

    if (error) {
      return <ErrorScreen />;
    }

    if (!sortedAttachments || sortedAttachments.length === 0) {
      return (
        <Grid container direction="column" alignItems="center" justifyContent="center" className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <Note height="28px" />
          </div>
          <Typography variant="h3">No documents yet</Typography>
          <Typography variant="body2">When documents become available, they will appear here.</Typography>
        </Grid>
      );
    }

    return (
      <div className={classes.page}>
        <Typography variant={isSmUp ? 'h1' : 'h2'} className={classes.header}>
          Documents
        </Typography>
        <Typography variant="body2" className={classes.subheader}>
          {sortedAttachments.length} {sortedAttachments.length === 1 ? 'document' : 'documents'} available
        </Typography>
        <div className={classes.grid} data-testid="attachments-list">
          {sortedAttachments.map((attachment) => (
            <button
              key={attachment.id}
              type="button"
              className={classes.card}
              onClick={() => push(generatePath(NavigationPath.DOCUMENT_DETAIL, { id: attachment.attachmentId }))}
              data-testid={`attachment-item-${attachment.id}`}
              aria-label={`View document ${attachment.fileName}`}
            >
              <div className={classes.iconWrap}>
                <Note height="20px" />
              </div>
              <div className={classes.cardBody}>
                <Typography variant="body1" className={classes.cardTitle}>
                  {attachment.fileName}
                </Typography>
                <Typography variant="caption" className={classes.cardMeta}>
                  {formatDate(attachment.created_at)}
                </Typography>
              </div>
              <ChevronRight height="20px" className={classes.chevron} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return <PortalContainer>{renderContent()}</PortalContainer>;
};

export default Documents;
